import {
  CheckCircle,
  Info,
  Lightbulb,
  Warning,
  WarningOctagon,
  X,
  XCircle,
} from "@phosphor-icons/react";
import * as React from "react";
import toast, {
  useToaster,
  type ToastPosition,
  type Toast,
} from "react-hot-toast/headless";
import { Button } from "../button/Button.js";
import { Text } from "../text/Text.js";
import { useThemeContext } from "../theme/Theme.js";

export type Renderable = React.JSX.Element | string | null;
export type ValueFunction<TValue, TArg> = (arg: TArg) => TValue;
export type ValueOrFunction<TValue, TArg> =
  | TValue
  | ValueFunction<TValue, TArg>;

const isFunction = <TValue, TArg>(
  valOrFunction: ValueOrFunction<TValue, TArg>,
): valOrFunction is ValueFunction<TValue, TArg> =>
  typeof valOrFunction === "function";

export const resolveValue = <TValue, TArg>(
  valOrFunction: ValueOrFunction<TValue, TArg>,
  arg: TArg,
): TValue => (isFunction(valOrFunction) ? valOrFunction(arg) : valOrFunction);

interface ToastWrapperProps {
  id: string;
  className?: string;
  style?: React.CSSProperties;
  onHeightUpdate: (id: string, height: number) => void;
  children?: React.ReactNode;
}

interface ToasterProps {
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  gutter?: number;
  reverseOrder?: boolean;
  children?: (toast: Toast) => React.JSX.Element;
}

const ToastWrapper = ({
  id,
  className,
  style,
  onHeightUpdate,
  children,
}: ToastWrapperProps) => {
  const ref = React.useCallback(
    (el: HTMLElement | null) => {
      if (el) {
        const updateHeight = () => {
          const height = el.getBoundingClientRect().height;
          onHeightUpdate(id, height);
        };
        updateHeight();
        new MutationObserver(updateHeight).observe(el, {
          subtree: true,
          childList: true,
          characterData: true,
        });
      }
    },
    [id, onHeightUpdate],
  );

  const { primaryColor, secondaryColor } = useThemeContext();

  return (
    <div
      ref={ref}
      data-primary-color={primaryColor}
      data-secondary-color={secondaryColor}
      className={className}
      style={style}
    >
      {children}
    </div>
  );
};

export const prefersReducedMotion = (() => {
  // Cache result
  let shouldReduceMotion: boolean | undefined = undefined;

  return () => {
    if (shouldReduceMotion === undefined && typeof window !== "undefined") {
      const mediaQuery = matchMedia("(prefers-reduced-motion: reduce)");
      shouldReduceMotion = !mediaQuery || mediaQuery.matches;
    }
    return shouldReduceMotion;
  };
})();

const getPositionStyle = (
  position: ToastPosition,
  offset: number,
): React.CSSProperties => {
  const top = position.includes("top");
  const verticalStyle: React.CSSProperties = top ? { top: 0 } : { bottom: 0 };
  const horizontalStyle: React.CSSProperties = position.includes("center")
    ? {
        justifyContent: "center",
      }
    : position.includes("right")
      ? {
          justifyContent: "flex-end",
        }
      : {};
  return {
    left: 0,
    right: 0,
    display: "flex",
    position: "absolute",
    transition: prefersReducedMotion()
      ? undefined
      : `all 230ms cubic-bezier(.21,1.02,.73,1)`,
    transform: `translateY(${offset * (top ? 1 : -1)}px)`,
    ...verticalStyle,
    ...horizontalStyle,
  };
};

interface ToastBarProps {
  toast: Toast;
  position?: ToastPosition;
  style?: React.CSSProperties;
  children?: (components: {
    icon?: Renderable;
    message: Renderable;
  }) => Renderable;
}

export const ToastBar: React.FC<ToastBarProps> = React.memo(
  ({ toast, position, style, children }) => {
    //   const icon = <ToastIcon toast={toast} />;
    const message = (
      <div {...toast.ariaProps} data-type={toast.type}>
        {resolveValue(toast.message, toast)}
      </div>
    );

    return (
      <div
        className={toast.className}
        style={{
          ...style,
          ...toast.style,
        }}
      >
        {typeof children === "function" ? (
          children({
            //   icon,
            message,
          })
        ) : (
          <>
            {/* {icon} */}
            {message}
          </>
        )}
      </div>
    );
  },
);

const Toaster = (props: ToasterProps) => {
  const {
    position = "bottom-right",
    gutter = 8,
    reverseOrder,
    children,
  } = props;
  const { toasts, handlers } = useToaster();
  const { startPause, endPause } = handlers;

  const DEFAULT_OFFSET = 16;

  return (
    <div
      style={{
        position: "fixed",
        zIndex: "var(--z-index-toast)",
        top: DEFAULT_OFFSET,
        left: DEFAULT_OFFSET,
        right: DEFAULT_OFFSET,
        bottom: DEFAULT_OFFSET,
        pointerEvents: "none",
      }}
      onMouseEnter={startPause}
      onMouseLeave={endPause}
    >
      {toasts.map((t) => {
        const toastPosition = t.position || position;
        const offset = handlers.calculateOffset(t, {
          reverseOrder,
          gutter,
          defaultPosition: position,
        });
        const positionStyle = getPositionStyle(toastPosition, offset);

        return (
          <ToastWrapper
            id={t.id}
            key={t.id}
            onHeightUpdate={handlers.updateHeight}
            style={positionStyle}
          >
            {t.type === "custom" ? (
              resolveValue(t.message, t)
            ) : children ? (
              children(t)
            ) : (
              <ToastBar toast={t} position={toastPosition} />
            )}
          </ToastWrapper>
        );
      })}
    </div>
  );
};

interface CustomToast {
  type?: "neutral" | "warning" | "danger" | "success";
  title?: string;
  messagePrefix?: string;
  duration?: number;
  withCloseIcon?: boolean;
  id?: string;
  message: ValueOrFunction<Renderable, Toast>;
  position?: ToastPosition;
}

interface Notification {
  title: string;
  duration?: number;
  withCloseIcon?: boolean;
  withIcon?: boolean;
  id?: string;
  message?: ValueOrFunction<Renderable, Toast>;
  position?: ToastPosition;
  withActions?: {
    primary?: {
      label: string;
      onClick: () => void;
    };
    secondary?: {
      label: string;
      onClick: () => void;
    };
  };
}

const createToast = (args: CustomToast) => {
  const {
    title,
    message,
    duration,
    messagePrefix,
    type = "neutral",
    withCloseIcon = true,
    position,
  } = args;
  return toast.custom(
    (t) => (
      <div className="ox-reset ox-ToastItem" data-type={type}>
        <div className="ox-reset ox-ToastItemIcon">
          {type === "neutral" ? (
            <Lightbulb size={24} fill="currentColor" />
          ) : type === "success" ? (
            <CheckCircle size={24} fill="currentColor" />
          ) : type === "danger" ? (
            <WarningOctagon size={24} fill="currentColor" />
          ) : type === "warning" ? (
            <Warning size={24} fill="currentColor" />
          ) : null}
        </div>
        <div className="ox-reset ox-ToastItemContent">
          {title && <h3 className="ox-reset ox-ToastTitle">{title}</h3>}
          <div
            {...t.ariaProps}
            data-type={t.type}
            className="ox-reset ox-ToastMessage"
          >
            {messagePrefix ? (
              <>
                <span className="ox-reset ox-ToastMessagePrefix">
                  {messagePrefix}
                </span>{" "}
              </>
            ) : null}
            {resolveValue(message, t)}
          </div>
        </div>
        {withCloseIcon ? (
          <button
            className="ox-reset ox-ToastItemClose"
            onClick={() => toast.remove(t.id)}
          >
            <XCircle size={24} fill="currentColor" />
          </button>
        ) : null}
      </div>
    ),
    {
      duration,
      position,
    },
  );
};

const createNotification = (args: Notification) => {
  const {
    title,
    message,
    duration,
    withActions,
    withIcon = true,
    withCloseIcon = true,
    position,
  } = args;
  return toast.custom(
    (t) => (
      <div className="ox-reset ox-NotificationItem">
        {withIcon ? (
          <div className="ox-reset ox-NotificationItemIcon">
            <Info size={20} fill="currentColor" />
          </div>
        ) : null}
        <div className="ox-reset ox-NotificationItemContent">
          <div className="ox-reset ox-NotificationItemHeader">
            <Text
              fontWeight="semibold"
              size="lg"
              className="ox-reset ox-NotificationItemTitle"
            >
              {title}
            </Text>
            {withCloseIcon ? (
              <button
                className="ox-reset ox-NotificationItemClose"
                onClick={() => toast.remove(t.id)}
              >
                <X size={16} fill="currentColor" />
              </button>
            ) : null}
          </div>

          <div
            {...t.ariaProps}
            data-type={t.type}
            className="ox-reset ox-NotificationItemMessage"
          >
            <Text size="sm" fontWeight="regular">
              {resolveValue(message, t)}
            </Text>
          </div>
          {withActions?.primary || withActions?.secondary ? (
            <div className="ox-reset ox-NotificationItemActions">
              {withActions?.secondary ? (
                <Button
                  size="sm"
                  variant="tertiary"
                  className="ox-reset ox-NotificationItemAction"
                  onClick={() => withActions?.secondary?.onClick()}
                >
                  {withActions.secondary.label}
                </Button>
              ) : null}
              {withActions?.primary ? (
                <Button
                  color="primary"
                  variant="primary"
                  size="sm"
                  className="ox-reset ox-NotificationItemAction"
                  onClick={() => withActions?.primary?.onClick()}
                >
                  {withActions.primary.label}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    ),
    {
      duration,
      position,
    },
  );
};
Toaster.displayName = "Toast";
createToast.displayName = "createToast";
createNotification.displayName = "createNotification";

export { Toaster, createToast, createNotification };
export type { ToasterProps };
